import { api } from './api'

export const isPushSupported = () =>
  'serviceWorker' in navigator && 'PushManager' in window

export const registerServiceWorker = async (onUpdate?: () => void) => {
  if (!isPushSupported()) return null
  const reg = await navigator.serviceWorker.register('/sw.js')

  const notifyUpdate = () => onUpdate?.()

  if (reg.waiting) {
    notifyUpdate()
  }

  reg.addEventListener('updatefound', () => {
    const newWorker = reg.installing
    if (!newWorker) return
    newWorker.addEventListener('statechange', () => {
      if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
        notifyUpdate()
      }
    })
  })

  // SPA never does a real page load, so we must poll for SW updates manually
  setInterval(() => reg.update(), 60 * 1000)

  return reg
}

export const applySwUpdate = async () => {
  const reg = await navigator.serviceWorker.getRegistration()
  if (reg?.waiting) {
    reg.waiting.postMessage({ type: 'SKIP_WAITING' })
  }
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    window.location.reload()
  })
}

export const getSubscriptionStatus = async (): Promise<boolean> => {
  if (!isPushSupported()) return false
  const reg = await navigator.serviceWorker.ready
  const sub = await reg.pushManager.getSubscription()
  return !!sub
}

export const subscribeToPush = async (): Promise<boolean> => {
  if (!isPushSupported()) return false
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return false

  const { data } = await api.get('/notifications/vapid-key')
  const reg = await navigator.serviceWorker.ready

  // Unsubscribe from any existing push subscription before subscribing with current VAPID key
  const existing = await reg.pushManager.getSubscription()
  if (existing) await existing.unsubscribe().catch(() => {})

  const subscription = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(data.publicKey),
  })

  await api.post('/notifications/subscribe', subscription.toJSON())
  return true
}

// Re-sends existing browser subscription to server (fixes lost subscriptions after server 410 cleanup)
export const syncSubscription = async (): Promise<void> => {
  if (!isPushSupported()) return
  const token = localStorage.getItem('token')
  if (!token) return
  const reg = await navigator.serviceWorker.ready
  const subscription = await reg.pushManager.getSubscription()
  if (!subscription) return
  try {
    await api.post('/notifications/subscribe', subscription.toJSON())
  } catch {
    // silently ignore — not critical
  }
}

export const unsubscribeFromPush = async (): Promise<boolean> => {
  if (!isPushSupported()) return false
  const reg = await navigator.serviceWorker.ready
  const subscription = await reg.pushManager.getSubscription()
  if (!subscription) return false
  await api.delete('/notifications/subscribe', { data: { endpoint: subscription.endpoint } })
  await subscription.unsubscribe()
  return true
}

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  const output = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; i++) output[i] = rawData.charCodeAt(i)
  return output
}
